/**
 * Sync Results Modal
 *
 * Displays detailed results after a platform sync completes.
 * Shows connection info, summary stats, and any conflicts or errors.
 *
 * Replaces quick-dismiss toast notification with persistent modal for better UX.
 */

import { Modal, Space, Typography, Table, Statistic, Row, Col, Button } from 'antd'
import { CheckCircleOutlined, CloseCircleOutlined, WarningOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { PullResult } from '../types'

const { Text, Title } = Typography

export interface SyncResultsModalProps {
  /** Whether the modal is visible */
  visible: boolean

  /** Connection name (e.g., "Square POS - Main Store") */
  connectionName: string

  /** Platform type (e.g., "square", "quickbooks") */
  platform: string

  /** Sync result data from /integrations/pull */
  result: PullResult

  /** Whether sync had errors (affects icon/messaging) */
  hasErrors?: boolean

  /** Callback when user closes the modal */
  onClose: () => void

  /** Optional callback when user clicks "Review Drafts" */
  onReviewDrafts?: () => void
}

interface ConflictItem {
  offeringUid: string
  reason: string
}

export function SyncResultsModal({
  visible,
  connectionName,
  platform,
  result,
  hasErrors = false,
  onClose,
  onReviewDrafts,
}: SyncResultsModalProps) {
  const hasConflicts = (result.conflicts?.length ?? 0) > 0
  const isSuccess = !hasErrors && !hasConflicts
  const isPartialSuccess = !hasErrors && hasConflicts
  const hasDrafts = result.itemsCreated > 0 || result.itemsUpdated > 0

  // Table columns for conflicts
  const columns: ColumnsType<ConflictItem> = [
    {
      title: 'Offering UID',
      dataIndex: 'offeringUid',
      key: 'offeringUid',
      width: 250,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: 'Conflict Reason',
      dataIndex: 'reason',
      key: 'reason',
      render: (text: string) => <Text type="warning">{text}</Text>,
    },
  ]

  // Determine title icon and color
  const titleIcon = isSuccess ? (
    <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
  ) : isPartialSuccess ? (
    <WarningOutlined style={{ fontSize: 24, color: '#faad14' }} />
  ) : (
    <CloseCircleOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />
  )

  return (
    <Modal
      open={visible}
      title={
        <Space>
          {titleIcon}
          <span>Sync Complete - {connectionName}</span>
        </Space>
      }
      onCancel={onClose}
      footer={[
        hasDrafts && onReviewDrafts && (
          <Button key="review" type="default" onClick={onReviewDrafts}>
            Review Drafts
          </Button>
        ),
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ].filter(Boolean)}
      width={700}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Platform Info */}
        <Text type="secondary">
          Platform: <Text strong style={{ color: '#262626' }}>{platform.toUpperCase()}</Text>
        </Text>

        {/* Summary Statistics */}
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="Pulled"
              value={result.itemsPulled}
              valueStyle={{ color: '#262626' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Created"
              value={result.itemsCreated}
              valueStyle={{ color: '#389e0d' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Updated"
              value={result.itemsUpdated}
              valueStyle={{ color: '#096dd9' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Skipped"
              value={result.itemsSkipped}
              valueStyle={{ color: '#8c8c8c' }}
            />
          </Col>
        </Row>

        {/* Success/Error Summary */}
        <Space direction="vertical" size="small">
          {isSuccess && (
            <Text type="success">
              <CheckCircleOutlined /> Successfully synced {result.itemsPulled} items from {connectionName}.
              {hasDrafts && ` ${result.itemsCreated} new, ${result.itemsUpdated} updated.`}
            </Text>
          )}

          {isPartialSuccess && (
            <Text type="warning">
              <WarningOutlined /> Sync completed with {result.conflicts?.length} conflicts.
              {hasDrafts && ` ${result.itemsCreated} new, ${result.itemsUpdated} updated.`}
            </Text>
          )}

          {hasErrors && (
            <Text type="danger">
              <CloseCircleOutlined /> Sync completed with errors. Some items may not have been processed.
            </Text>
          )}
        </Space>

        {/* Conflicts Table */}
        {hasConflicts && (
          <div>
            <Title level={5}>
              <WarningOutlined style={{ color: '#faad14' }} /> Conflicts ({result.conflicts?.length})
            </Title>
            <Table
              columns={columns}
              dataSource={result.conflicts}
              rowKey="offeringUid"
              pagination={false}
              size="small"
              scroll={{ y: 300 }}
            />
          </div>
        )}

        {/* Help Text */}
        {hasDrafts && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Tip: Review drafts in the CATALOG tab before activating them.
          </Text>
        )}
      </Space>
    </Modal>
  )
}
